import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { X, Heart, Share2, Star, ShieldCheck, Truck, RotateCcw, CreditCard, Minus, Plus, ShoppingCart, Scale, ShoppingBag, Store, BadgeCheck, Box } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import '@google/model-viewer';
import 'react-toastify/dist/ReactToastify.css';
import { STORAGE_BASE } from '../config';
import Skeleton from './Skeleton';
import OrderFormModal from './OrderFormModal';
import ARTryOn from './ARTryOn';
import CompareModal from './CompareModal';
import { CommentsBox } from './CommentsBox';
import { RowView, applySeo } from '../pages/HomePage';
import { setJsonLd, siteOrigin } from './SeoJsonLd';
import VariantSelector from './VariantSelector';
import CustomerAccount from './CustomerAccount';
/* vibefarsi store anatomy: breadcrumb / rating / price / tabs / product-grid */
import { Breadcrumb } from './ui/breadcrumb';
import { Rating } from './ui/rating';
import { Price } from './ui/price';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { ProductGrid } from './blocks/product-grid';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [arOpen, setArOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [variantSel, setVariantSel] = useState({});
  const [tpl, setTpl] = useState([]);
  useEffect(() => {
    axios.get('/site-settings').then(async r => {
      const slug = r.data?.product_detail_page;
      if (!slug) return;
      try {
        const pg = await axios.get(`/pages/${slug}`);
        const vw = typeof window !== 'undefined' && window.innerWidth < 768 ? 'mobile' : 'desktop';
        const rows = (pg.data?.platforms?.[vw] || pg.data?.platforms?.desktop || []).filter(x => x.enabled !== false);
        setTpl(rows);
      } catch {}
    }).catch(() => {});
  }, []);
  const [loginOpen, setLoginOpen] = useState(false);
  const [cmpBusy, setCmpBusy] = useState(false);
  const [wish, setWish] = useState(false);
  const [related, setRelated] = useState([]);
  const [rating, setRating] = useState({ avg: 0, count: 0 });

  useEffect(() => {
    try { setWish((JSON.parse(localStorage.getItem('wishlist') || '[]')).includes(Number(id))); } catch {}
  }, [id]);
  const toggleWish = () => {
    try {
      const l = JSON.parse(localStorage.getItem('wishlist') || '[]');
      const n = Number(id);
      const has = l.includes(n);
      localStorage.setItem('wishlist', JSON.stringify(has ? l.filter(x => x !== n) : [...l, n]));
      setWish(!has);
      toast.success(has ? 'از علاقه‌مندی‌ها حذف شد.' : 'به علاقه‌مندی‌ها اضافه شد.');
    } catch {}
  };
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) { await navigator.share({ title: product?.name, url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success('لینک محصول کپی شد.');
    } catch {}
  };

  useEffect(() => {
    axios.get(`/products/${id}`).then(r => {
      setProduct(r.data);
      applySeo(r.data || {}, r.data?.name);
      setQty(Math.max(r.data?.min_purchase || 1, 1));
      setVariantSel({});
      setLoading(false);
      // related: same category first product list
      const cid = r.data?.categories?.[0]?.id;
      if (cid) axios.get('/products', { params: { category_id: cid, per_page: 5 } })
        .then(x => setRelated((x.data?.data || x.data || []).filter(p => p.id !== r.data.id).slice(0, 4))).catch(() => {});
      // rating summary (from approved comments count; stars when rating column exists)
      axios.get('/comments/product/' + id, { params: { per_page: 1 } })
        .then(x => setRating({ avg: 0, count: x.data?.total || 0 })).catch(() => {});
    }).catch(() => setLoading(false));
  }, [id]);

  const authH = () => ({ headers: { Authorization: 'Bearer ' + (localStorage.getItem('customer_token') || '') } });
  const needLogin = () => {
    if (!localStorage.getItem('customer_token')) { setLoginOpen(true); return true; }
    return false;
  };
  const needVariant = ((product && product.variants) || []).length > 0;
  const variantReady = !needVariant || Object.keys(variantSel || {}).length > 0;
  const addCart = async () => {
    if (needLogin()) return;
    if (needVariant && !variantReady) { toast.error('ترکیب (رنگ/سایز) را انتخاب کنید.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); return; }
    if (((product.customization_data?.fields || []).length > 0) || (product.order_form && (product.order_form.fields || []).length > 0)) {
      setModalOpen(true);
      return;
    }
    const min = Math.max(product.min_purchase || 1, 1);
    const max = Math.max(product.max_purchase || 100, min);
    const q = Math.min(Math.max(qty, min), max);
    setBusy(true);
    try {
      await axios.post('/cart/add', { product_id: product.id, qty: q, ...(needVariant ? { attributes: { _variant: variantSel } } : {}) }, authH());
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('به سبد خرید اضافه شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } catch (e) { toast.error(e.response?.data?.message || 'خطا در افزودن به سبد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); }
    setBusy(false);
  };
  const quickAdd = async (p) => {
    if (needLogin()) return;
    try {
      await axios.post('/cart/add', { product_id: Number(p.id), qty: 1 }, authH());
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('به سبد خرید اضافه شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
    } catch (e) { toast.error(e.response?.data?.message || 'خطا در افزودن به سبد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); }
  };
  const addCompare = async () => {
    if (needLogin()) return;
    if (cmpBusy) return;
    setCmpBusy(true);
    try {
      await axios.post('/compare/add', { product_id: product.id }, authH());
      window.dispatchEvent(new Event('compare-updated'));
      toast.success('به مقایسه اضافه شد.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' });
      setCompareOpen(true);
    } catch (e) { toast.error(e.response?.data?.message || 'خطا در افزودن به مقایسه.', { position: 'bottom-right', autoClose: 5000, theme: 'colored' }); }
    setCmpBusy(false);
  };

  if (loading) return (
    <div className="mx-auto px-4 py-8" dir="rtl" style={{ maxWidth: 'none' }}>
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-4"><Skeleton height={420} style={{ borderRadius: 20 }} /></div>
        <div className="lg:col-span-5"><Skeleton height={28} width="70%" /><Skeleton height={16} width="40%" /><Skeleton height={120} style={{ borderRadius: 14 }} /></div>
        <div className="lg:col-span-3"><Skeleton height={320} style={{ borderRadius: 20 }} /></div>
      </div>
    </div>
  );
  if (!product) return <div style={{ padding: 100, textAlign: 'center' }}>محصول یافت نشد.</div>;

  const isLoggedIn = !!localStorage.getItem('customer_token');
  const handleBuy = () => { if (!isLoggedIn) { setLoginOpen(true); } else { setModalOpen(true); } };

  const media = product.media || [];
  const allMedia = product.image_path ? [{ type: 'image', path: product.image_path }, ...media] : media;
  const activeMedia = allMedia[activeIdx] || allMedia[0];
  const mainSrc = activeMedia?.path ? `${STORAGE_BASE}/${String(activeMedia.path).replace(/^\/+/, '')}` : 'https://via.placeholder.com/600x600?text=Silver';
  const out = (product.stock || 0) < 1;
  const hasCustom = (product.customization_data?.fields || []).length > 0;
  const basePrice = product.customization_data?.base_price || product.price || 0;
  const catName = product.categories?.[0]?.name;
  const attrs = product.attributes && Object.keys(product.attributes).length > 0 ? Object.entries(product.attributes) : [];
  const hasCommentsTpl = (tpl || []).some(r => (r.columns || []).some(c => (c.widgets || []).some(w => w.type === 'post_comments' && w.enabled !== false)));
  // GEO/AEO: Product + BreadcrumbList
  const ldProduct = (() => {
    try {
      const origin = siteOrigin();
      const url = `${origin}/products/${product.id}`;
      const img = mainSrc && !/^https?:\/\//.test(mainSrc) ? origin + (mainSrc.startsWith('/') ? mainSrc : `/${mainSrc}`) : mainSrc;
      const crumbs = [
        { '@type': 'ListItem', position: 1, name: 'فروشگاه', item: `${origin}/products` },
        ...(catName ? [{ '@type': 'ListItem', position: 2, name: catName, item: `${origin}/products?category=${product.categories[0].id}` }] : []),
        { '@type': 'ListItem', position: catName ? 3 : 2, name: product.name, item: url },
      ];
      return {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Product', '@id': `${url}#product`, name: product.name, url,
            ...(img ? { image: [img] } : {}),
            ...(product.description ? { description: String(product.description).slice(0, 2000) } : {}),
            ...(catName ? { category: catName } : {}),
            brand: { '@type': 'Brand', name: (typeof document !== 'undefined' && document.title) ? document.title : '' },
            offers: {
              '@type': 'Offer', url, priceCurrency: 'IRR', price: Number(basePrice || 0),
              availability: out ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
            },
          },
          { '@type': 'BreadcrumbList', itemListElement: crumbs },
        ],
      };
    } catch { return null; }
  })();
  setJsonLd(`product-${product.id}`, ldProduct);
  const gridItems = related.map(r => ({
    id: String(r.id),
    name: r.name,
    price: Number(r.price || 0),
    image: r.image_path ? <img src={`${STORAGE_BASE}/${String(r.image_path).replace(/^\/+/, '')}`} alt={r.name} loading="lazy" className="size-full object-cover" /> : undefined,
  }));

  return (
    <div className="mx-auto px-4 pb-16 pt-4" dir="rtl" style={{ maxWidth: 'none' }}>
      <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" />
      {(product.customization_data?.ar_model || product.customization_data?.ar_image) && arOpen && (
        <div onClick={() => setArOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} role="dialog" aria-modal="true" aria-label="نمایش AR">
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(720px, 100%)', background: 'var(--card)', borderRadius: 'var(--shape-card)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 800 }}>
              <span>مشاهده در فضای واقعی</span>
              <button onClick={() => setArOpen(false)} aria-label="بستن" style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--fg)' }}>✕</button>
            </div>
            {product.customization_data?.ar_model && !['wrist', 'finger'].includes(product.customization_data?.ar_type) ? (
              <model-viewer
                src={product.customization_data.ar_model}
                poster={product.customization_data?.ar_image ? `${STORAGE_BASE}/${String(product.customization_data.ar_image).replace(/^\/+/, '')}` : mainSrc}
                ar ar-modes="webxr scene-viewer quick-look" camera-controls auto-rotate
                style={{ width: '100%', height: 'min(70vh, 520px)', background: 'var(--muted)' }}
              />
            ) : (
              <ARTryOn src={product.customization_data?.ar_image ? `${STORAGE_BASE}/${String(product.customization_data.ar_image).replace(/^\/+/, '')}` : mainSrc} name={product.name} arType={product.customization_data.ar_type} arModel={product.customization_data.ar_model} />
            )}
          </div>
        </div>
      )}
      <Breadcrumb items={[{ label: 'فروشگاه', href: '/products' }, ...(catName ? [{ label: catName, href: `/products?category=${product.categories[0].id}` }] : []), { label: product.name }]} />

      <div className="mt-5 grid gap-6 lg:grid-cols-12">
        {/* gallery */}
        <div className="lg:col-span-4">
          <div className="relative overflow-hidden rounded-2xl border border-border bg-card">
            <div className="aspect-square">
              <img src={mainSrc} alt={product.name} className="size-full object-cover" />
            </div>
            <div className="absolute end-3 top-3 flex gap-2">
              <button onClick={toggleWish} title="علاقه‌مندی" aria-label="علاقه‌مندی"
                className="flex size-10 items-center justify-center rounded-full border border-border bg-card/90 backdrop-blur"
                style={{ color: wish ? '#DC2626' : undefined }}>
                <Heart size={18} fill={wish ? '#DC2626' : 'none'} />
              </button>
              <button onClick={share} title="اشتراک‌گذاری" aria-label="اشتراک‌گذاری"
                className="flex size-10 items-center justify-center rounded-full border border-border bg-card/90 text-muted-foreground backdrop-blur">
                <Share2 size={18} />
              </button>
              {(product.customization_data?.ar_model || product.customization_data?.ar_image) && (
                <button onClick={() => setArOpen(true)} title="مشاهده AR" aria-label="مشاهده AR"
                  className="flex size-10 items-center justify-center rounded-full border border-border bg-card/90 text-muted-foreground backdrop-blur">
                  <Box size={18} />
                </button>
              )}
            </div>
            {out && <div className="absolute inset-0 flex items-center justify-center bg-background/70 font-bold">ناموجود</div>}
          </div>
          {allMedia.length > 1 && (
            <div className="mt-3 flex gap-2">
              {allMedia.map((m, i) => (
                <button key={i} onClick={() => setActiveIdx(i)} aria-label={`تصویر ${i + 1}`}
                  className="flex-1 overflow-hidden rounded-xl border-2 transition"
                  style={{ borderColor: i === activeIdx ? 'var(--primary)' : 'transparent' }}>
                  <img src={`${STORAGE_BASE}/${String(m.path).replace(/^\/+/, '')}`} alt="" loading="lazy" className="aspect-square w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div className="min-w-0 lg:col-span-5">
          <h1 className="text-xl font-extrabold leading-8 sm:text-2xl">{product.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {rating.count > 0 ? (
              <>
                <Rating value={4} readOnly size="sm" />
                <span>{Number(rating.count).toLocaleString('fa-IR')} دیدگاه</span>
              </>
            ) : <span>بدون دیدگاه — اولین نظر را شما بنویسید</span>}
            {(product.loyalty_points || 0) > 0 && (
              <Badge variant="outline">★ {Number(product.loyalty_points).toLocaleString('fa-IR')} امتیاز باشگاه</Badge>
            )}
          </div>

          {attrs.length > 0 && (
            <div className="mt-5">
              <h2 className="mb-2 text-sm font-bold">ویژگی‌ها</h2>
              <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-border bg-border text-sm">
                {attrs.slice(0, 6).map(([k, v]) => (
                  <div key={k} className="bg-card px-3 py-2.5">
                    <dt className="text-xs text-muted-foreground">{k}</dt>
                    <dd className="mt-0.5 font-semibold">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <Tabs defaultValue="intro" className="mt-5">
            <TabsList>
              <TabsTrigger value="intro">معرفی</TabsTrigger>
              {attrs.length > 0 && <TabsTrigger value="specs">مشخصات</TabsTrigger>}
              <TabsTrigger value="reviews">نظرات{rating.count > 0 ? ` (${Number(rating.count).toLocaleString('fa-IR')})` : ''}</TabsTrigger>
            </TabsList>
            <TabsContent value="intro">
              <p className="whitespace-pre-line text-sm leading-7 text-muted-foreground">{product.description || 'توضیحاتی برای این محصول ثبت نشده است.'}</p>
              {(product.description_rows || []).filter(r => r.enabled !== false).map((r, i) => (
                r.columns ? <RowView key={'d' + (r.id || i)} row={r} data={{ product }} /> : null
              ))}
              {hasCustom && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {product.customization_data.fields.map(f => (
                    <span key={f.name} className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">{f.name}</span>
                  ))}
                </div>
              )}
            </TabsContent>
            {attrs.length > 0 && (
              <TabsContent value="specs">
                <dl className="overflow-hidden rounded-2xl border border-border text-sm">
                  {attrs.map(([k, v], idx) => (
                    <div key={k} className="flex items-center justify-between gap-4 px-4 py-3" style={{ background: idx % 2 ? 'var(--muted)' : 'var(--card)' }}>
                      <dt className="text-muted-foreground">{k}</dt>
                      <dd className="font-semibold">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </TabsContent>
            )}
            <TabsContent value="reviews">
              {hasCommentsTpl
                ? <p className="text-sm text-muted-foreground">دیدگاه‌ها از بخش نظرات همین صفحه (قالب سفارشی) نمایش داده می‌شود.</p>
                : <CommentsBox type="product" id={product.id} title="دیدگاه‌های محصول" />}
            </TabsContent>
          </Tabs>
        </div>

        {/* seller box */}
        <aside className="h-fit rounded-2xl border border-border bg-card p-4 sm:p-5 lg:col-span-3 lg:sticky lg:top-4">
          <div className="flex items-center gap-2">
            <span className="flex size-9 items-center justify-center rounded-xl bg-muted"><Store size={17} /></span>
            <div className="min-w-0">
              <div className="truncate text-sm font-bold">فروشگاه رسمی</div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground"><BadgeCheck size={13} /> عملکرد عالی</div>
            </div>
          </div>
          <div className="mt-3 space-y-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
            <div>گارانتی اصالت و سلامت فیزیکی</div>
            <div className={out ? '' : 'font-semibold text-foreground'}>{out ? 'ناموجود' : 'موجود در انبار · ارسال ۲ تا ۴ روز کاری'}</div>
          </div>
          <div className="mt-3 border-t border-border pt-3">
            {hasCustom ? (
              <p className="text-sm font-semibold text-muted-foreground">قیمت پس از انتخاب ویژگی‌ها محاسبه می‌شود</p>
            ) : (() => {
              const disc = Math.min(99, Math.max(0, Number(product.customization_data?.discount_percent) || 0));
              const final = disc > 0 ? Math.round(Number(basePrice) * (100 - disc) / 100) : Number(basePrice);
              return disc > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ background: 'var(--destructive, #DC2626)', color: '#fff', borderRadius: 'var(--shape-btn)', padding: '3px 12px', fontSize: '0.8rem', fontWeight: 800 }}>{Number(disc).toLocaleString('fa-IR')}٪ تخفیف</span>
                  <span style={{ textDecoration: 'line-through', color: 'var(--muted-fg)', fontSize: '0.95rem' }}>{Number(basePrice).toLocaleString('fa-IR')} تومان</span>
                  <Price amount={final} size="lg" />
                </div>
              ) : <Price amount={Number(basePrice)} size="lg" />;
            })()}
          </div>
          <VariantSelector variants={product.variants} value={variantSel} onChange={setVariantSel} />
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">تعداد</span>
            <div className="flex items-center gap-3 rounded-xl border border-border px-2 py-1">
              <button onClick={() => setQty(q => Math.max(Math.max(product.min_purchase || 1, 1), q - 1))} aria-label="کمتر" className="p-1"><Minus size={15} /></button>
              <span className="min-w-6 text-center text-sm font-bold tabular-nums">{Number(qty).toLocaleString('fa-IR')}</span>
              <button onClick={() => setQty(q => Math.min(Math.max(product.max_purchase || 100, 1), q + 1))} aria-label="بیشتر" className="p-1"><Plus size={15} /></button>
            </div>
          </div>
          <Button onClick={addCart} disabled={busy || out} className="mt-3 w-full" size="lg">
            <ShoppingCart size={18} /> {out ? 'ناموجود' : busy ? '…' : hasCustom ? 'انتخاب ویژگی‌ها و افزودن' : 'افزودن به سبد'}
          </Button>
          <Button onClick={addCompare} disabled={cmpBusy} variant="outline" className="mt-2 w-full">
            <Scale size={17} /> مقایسه
          </Button>
          <p className="mt-3 text-center text-[11px] leading-5 text-muted-foreground">۷ روز ضمانت بازگشت · پرداخت امن<br />حداقل خرید {Number(product.min_purchase || 1).toLocaleString('fa-IR')} · حداکثر {Number(product.max_purchase || 100).toLocaleString('fa-IR')}</p>
        </aside>
      </div>

      {gridItems.length > 0 && (
        <ProductGrid title="کالاهای مشابه" products={gridItems} onAdd={quickAdd} />
      )}

      {tpl.filter(r => r.enabled !== false).map((r, i) => (
        r.columns ? <RowView key={'t' + (r.id || i)} row={r} data={{ product }} /> : null
      ))}

      {compareOpen && (
        <div onClick={() => setCompareOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(12,10,9,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, direction: 'rtl' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--card)', color: 'var(--fg)', border: '1px solid var(--border)', borderRadius: 20, width: 'min(640px, 96%)', maxHeight: '90vh', overflowY: 'auto', padding: 22, boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>مقایسه محصول</h2>
              <button onClick={() => setCompareOpen(false)} aria-label="بستن" style={{ width: 34, height: 34, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={16} /></button>
            </div>
            <p style={{ color: 'var(--muted-fg)', fontSize: '0.82rem', margin: '0 0 12px' }}>مقایسه با محصول فعلی ({product.name}) انجام می‌شود.</p>
            <button onClick={() => setCompareOpen(false)} style={{ padding: '10px 18px', borderRadius: 12, border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--fg)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}>بستن</button>
          </div>
        </div>
      )}
      <div className="prod-stickybar">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--muted-fg)' }}>{product.name}</div>
          <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--accent)' }}>
            {Number(basePrice).toLocaleString('fa-IR')} تومان
          </div>
        </div>
        <button onClick={addCart} disabled={busy || out}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '12px 22px', borderRadius: 12, border: 'none',
            background: out ? 'var(--muted)' : 'var(--primary)',
            color: out ? 'var(--muted-fg)' : 'var(--on-primary)',
            fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
          }}>
          <ShoppingCart size={17} />
          {out ? 'ناموجود' : 'افزودن به سبد'}
        </button>
      </div>
      <div className="prod-sticky-spacer" />
      <OrderFormModal product={product} open={modalOpen} onClose={() => setModalOpen(false)} variantSel={variantSel} />
      <LoginBridge active={loginOpen && !isLoggedIn} onDone={() => { setLoginOpen(false); setModalOpen(true); }} />
    </div>
  );
}

/* Global login bridge: no local modal — uses the single QuickModals login dialog. */
function LoginBridge({ active, onDone }) {
  useEffect(() => {
    if (!active) return;
    window.dispatchEvent(new CustomEvent('open-login', { detail: { flow: 'order' } }));
    const onUpd = () => onDone();
    window.addEventListener('cart-updated', onUpd);
    return () => window.removeEventListener('cart-updated', onUpd);
  }, [active]);
  return null;
}
